import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import {
  isSubscriptionImpaired,
  StudioMembership,
  studioInitials,
} from "../../helpers/identity_storage";

interface StudioMembershipCardProps {
  membership: StudioMembership;
  highlighted?: boolean;
  busy?: boolean;
  current?: boolean;
  onSelect: (membership: StudioMembership) => void;
  onAccept?: (membership: StudioMembership) => void;
  onDecline?: (membership: StudioMembership) => void;
}

const ROLE_I18N_KEYS: Record<string, string> = {
  owner: "auth.membership.role.owner",
  manager: "auth.membership.role.manager",
  receptionist: "auth.membership.role.receptionist",
  professional: "auth.membership.role.professional",
  sdr: "auth.membership.role.sdr",
};

const enterLabelKey = (role: string, current: boolean): string => {
  if (current) {
    return "auth.switch_studio.stay";
  }

  if (role === "professional") {
    return "auth.select_studio.enter_as_professional";
  }

  if (role === "receptionist") {
    return "auth.select_studio.enter_as_receptionist";
  }

  if (role === "owner" || role === "manager") {
    return "auth.select_studio.enter_as_owner";
  }

  return "auth.select_studio.enter";
};

const StudioMembershipCard = ({
  membership,
  highlighted = false,
  busy = false,
  current = false,
  onSelect,
  onAccept,
  onDecline,
}: StudioMembershipCardProps) => {
  const { t } = useTranslation();
  const primaryRole = membership.roles?.[0] || "";
  const roleLabel = ROLE_I18N_KEYS[primaryRole]
    ? t(ROLE_I18N_KEYS[primaryRole])
    : primaryRole;
  const isPending = membership.status === "pending";
  const isBlocked = membership.blocked || membership.status === "suspended";
  const subscriptionImpaired = isSubscriptionImpaired(membership);
  const studioName = membership.tenant_name || t("auth.select_studio.unnamed");
  const isOwnerLike = primaryRole === "owner" || primaryRole === "manager";

  return (
    <Card className={`border h-100 ${highlighted ? "border-primary shadow-sm" : ""}`}>
      <CardBody className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
          <div className="d-flex align-items-start gap-2">
            <div
              className="avatar-sm flex-shrink-0"
              aria-hidden
            >
              <span className="avatar-title rounded bg-primary-subtle text-primary fs-16">
                {studioInitials(studioName)}
              </span>
            </div>
            <div>
              <h5 className="mb-1">{studioName}</h5>
              {roleLabel ? (
                <Badge color="primary" pill className="bg-opacity-10 text-primary">
                  {roleLabel}
                </Badge>
              ) : null}
              {current ? (
                <Badge color="success" className="ms-1">
                  {t("auth.switch_studio.current")}
                </Badge>
              ) : null}
            </div>
          </div>
          {isPending ? (
            <Badge color="warning">{t("auth.membership.pending")}</Badge>
          ) : null}
          {isBlocked ? (
            <Badge color="danger">{t("auth.membership.blocked_badge")}</Badge>
          ) : null}
          {!isBlocked && subscriptionImpaired ? (
            <Badge color="warning">{t("auth.membership.subscription_badge")}</Badge>
          ) : null}
        </div>

        {membership.last_accessed_at ? (
          <p className="text-muted fs-13 mb-2">
            {t("auth.select_studio.last_access", {
              date: new Date(membership.last_accessed_at).toLocaleString(),
            })}
          </p>
        ) : (
          <p className="text-muted fs-13 mb-2">{t("auth.select_studio.no_last_access")}</p>
        )}

        {isBlocked ? (
          <p className="text-danger fs-13 mb-2">{t("auth.membership.blocked")}</p>
        ) : null}

        {!isBlocked && subscriptionImpaired ? (
          <div className="mb-3">
            <p className="text-warning fs-13 mb-1">
              {t("auth.membership.subscription_impaired")}
            </p>
            {current && isOwnerLike ? (
              <Button
                color="link"
                size="sm"
                className="p-0"
                tag={Link}
                to="/settings/subscription"
              >
                {t("auth.membership.subscription_cta_owner")}
              </Button>
            ) : (
              <p className="text-muted fs-12 mb-0">
                {isOwnerLike
                  ? t("auth.membership.subscription_cta_owner_later")
                  : t("auth.membership.subscription_cta_staff")}
              </p>
            )}
          </div>
        ) : null}

        <div className="mt-auto">
          {isPending ? (
            <div className="d-grid gap-2">
              <Button
                color="primary"
                disabled={busy || !membership.id || !onAccept}
                aria-label={t("auth.select_studio.accept_invite_aria", { name: studioName })}
                onClick={() => onAccept?.(membership)}
              >
                {t("auth.select_studio.accept_invite")}
              </Button>
              <Button
                color="light"
                disabled={busy || !membership.id || !onDecline}
                aria-label={t("auth.select_studio.decline_invite_aria", { name: studioName })}
                onClick={() => onDecline?.(membership)}
              >
                {t("auth.select_studio.decline_invite")}
              </Button>
            </div>
          ) : (
            <Button
              color="primary"
              className="w-100"
              disabled={busy || isBlocked}
              aria-label={`${t(enterLabelKey(primaryRole, current))} — ${studioName}`}
              onClick={() => onSelect(membership)}
            >
              {t(enterLabelKey(primaryRole, current))}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default StudioMembershipCard;
